import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Modal,
    Button,
    Checkbox,
    FormControlLabel
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";


import { PlanRelatedFactorsUpdate, PlanrelatedId,
    NursingDiagnosisSearchDropdown,
    CarePlanTypeSearchDropdown } from "../../../services/nursing/NursingCarePlanServices";
import { ModalStyle } from "../../../../common/components/ModalStyle";
import { CloseModalIcon } from "../../../../common/components/assets/commonassets/CommonAssets";
import InputField from "../../../../common/components/FormFields/InputField";
import SearchDropdown from "./searchDropdown";



export default function PlanRelatedEditDialog({
    open,
    onClose,
    data,
    setSnack,
    onUpdateSuccess
}) {

    const [confirmUpdateOpen, setConfirmUpdateOpen] = useState(false);
    const initialDataRef = useRef(null);
    const [selectedDiagnosisId, setSelectedDiagnosisId] = useState(null);
    const [selectedCarePlanTypeId, setSelectedCarePlanTypeId] = useState(null);
    const [searchBarString, setSearchBarString] = useState("");
    const [carePlanTypeOptions, setCarePlanTypeOptions] = useState([]);
    const [diagnosisSearch, setDiagnosisSearch] = useState("");
    const [diagnosisOptions, setDiagnosisOptions] = useState([]);

    const defaultFormValues = {
        description: "",
        diagnosis: null,
        carePlanType: null,
        active: false
    };

    const { control, watch, setValue, reset } = useForm({
        defaultValues: defaultFormValues,
    });


    const handleNursingDiagnosisSearch = (inputValue) => {
        if (typeof inputValue !== "string") return;

        const trimmed = inputValue.trim();

        setSearchBarString(trimmed);

        if (!trimmed) {
            setDiagnosisOptions([]);
            return;
        }

        NursingDiagnosisSearchDropdown(trimmed)
            .then((res) => {
                setDiagnosisOptions(res?.data?.result ?? []);
            })
            .catch(() => {
                setDiagnosisOptions([]);
            });
    };


    const handleCarePlanType = (inputValue) => {
        if (typeof inputValue !== "string") return;

        const trimmed = inputValue.trim();

        setSearchBarString(trimmed);

        if (!trimmed) {
            setCarePlanTypeOptions([]);
            return;
        }

        CarePlanTypeSearchDropdown(trimmed)
            .then((res) => {
                setCarePlanTypeOptions(res?.data?.result ?? []);
            })
            .catch(() => {
                setCarePlanTypeOptions([]);
            });
    };

    useEffect(() => {

        const fetchRelatedFactors = async () => {

            if (!data?.id && !data?.Id) return;

            try {

                const id = data?.id ?? data?.Id;

                const response = await PlanrelatedId(id);
                const editData = response?.data?.result?.[0];

                if (!editData) return;

                const diagRes =
                    await NursingDiagnosisSearchDropdown(
                        editData["Nursing Diagnosis"]
                    );

                const diagnosisOption =
                    diagRes?.data?.result?.[0] || null;

                setDiagnosisOptions(
                    diagnosisOption ? [diagnosisOption] : []
                );

                const careRes =
                    await CarePlanTypeSearchDropdown(
                        editData["Care Plan Type"]
                    );

                const carePlanOption =
                    careRes?.data?.result?.[0] || null;

                setCarePlanTypeOptions(
                    carePlanOption ? [carePlanOption] : []
                );

                const formattedData = {
                    description: editData.Description || "",
                    diagnosis: diagnosisOption,
                    carePlanType: carePlanOption,
                    active: editData.Active ?? false
                };

                initialDataRef.current = formattedData;

                reset(formattedData);

            } catch (error) {
                console.error("Edit fetch error", error);
            }
        };

        if (open) {
            fetchRelatedFactors();
        }

    }, [open, data, reset]);

    const handleUpdate = async () => {
        try {

            const values = watch();

            const payload = {
                id: data?.id ?? data?.Id,
                description: values.description || "",
                active: values.active ?? false,
                nursingDiagnosisId: values.diagnosis?.id || 0,
                carePlanTypeId: values.carePlanType?.id || 0,
            };

            const res = await PlanRelatedFactorsUpdate(payload);

            // ✅ USE API MESSAGE
            setSnack({
                open: true,
                message:
                    res?.data?.message ||
                    "NursingPlanRelatedFactors updated successfully",
                severity: "success",
            });

            // ✅ instant table update
            onUpdateSuccess({
                id: payload.id,
                description: payload.description,
                active: payload.active,
                nursingDiagnosis: values.diagnosis?.value,
                carePlanType: values.carePlanType?.value,
            });

            setConfirmUpdateOpen(false);
            onClose();

        } catch (error) {

            setSnack({
                open: true,
                message:
                    error?.response?.data?.message ||
                    "Update failed",
                severity: "error",
            });
        }
    };


    const resetAllData = () => {
        reset(defaultFormValues);
    };

    return (
        <>
            <Modal open={open} onClose={onClose}>
                <Box sx={ModalStyle} className="w-[650px] space-y-4">

                    <div className="flex justify-between">
                        <label className="font-semibold text-lg">
                            Related Factors Details
                        </label>
                        <CloseModalIcon onClick={onClose} />
                    </div>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 1
                        }}
                    >

                        <InputField
                            control={control}
                            name="description"
                            label="Description"
                            placeholder="Description"
                        />

                        <SearchDropdown
                            control={control}
                            name="diagnosis"
                            placeholder="Diagnosis"
                            menuPlacement="bottom"
                            isClearable
                            dataArray={diagnosisOptions}
                            inputValue={diagnosisSearch}
                            handleInputChange={handleNursingDiagnosisSearch}
                            getOptionLabel={(option) => option?.value || ""}
                        />

                        <SearchDropdown
                            control={control}
                            name="carePlanType"
                            placeholder="Care Plan Type"
                            menuPlacement="bottom"
                            dataArray={carePlanTypeOptions}
                            isClearable
                            inputValue={searchBarString}
                            handleInputChange={handleCarePlanType}
                            getOptionLabel={(option) => option?.value || ""}
                        />

                        <Controller
                            name="active"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.value}
                                            onChange={(e) =>
                                                field.onChange(e.target.checked)
                                            }
                                            sx={{
                                                color: "#1e3a8a",
                                                "&.Mui-checked": {
                                                    color: "#1e3a8a"
                                                }
                                            }}
                                        />
                                    }
                                    label="Active"
                                />
                            )}
                        />

                    </Box>

                    <div className="flex justify-end gap-2 mt-6">

                        <button
                            onClick={resetAllData}
                            className="h-[36px] px-4 rounded border border-red-500 text-red-500"
                        >
                            Reset
                        </button>

                        <button
                            onClick={() => setConfirmUpdateOpen(true)}
                            className="h-[36px] px-4 rounded text-white bg-blue-900"
                        >
                            Update
                        </button>

                    </div>

                </Box>
            </Modal>

            <Modal open={confirmUpdateOpen}>
                <Box
                    sx={{
                        width: 400,
                        bgcolor: "background.paper",
                        p: 3,
                        borderRadius: 2,
                        mx: "auto",
                        mt: "20%",
                    }}
                >
                    <h3 className="font-semibold mb-3">
                        Confirm Update
                    </h3>

                    <p className="mb-4">
                        Are you sure you want to update?
                    </p>

                    <div className="flex justify-end gap-2">
                        <Button
                            color="success"
                            onClick={() =>
                                setConfirmUpdateOpen(false)
                            }
                        >
                            Cancel
                        </Button>

                        <Button
                            variant="contained"
                            color="success"
                            onClick={handleUpdate}
                        >
                            Confirm
                        </Button>
                    </div>
                </Box>
            </Modal>
        </>
    );
}